import type { MilestonePriority } from './project-controls.js';
import type { ExceptionReportSnapshot } from './project-change.js';
/** PRINCE2 governance lifecycle of a project. It runs next to the commercial `ProjectStatus`. */
export const ProjectGovernanceState = {
  Preparation: 'preparation',
  AwaitingInitiationAuthorization: 'awaiting_initiation_authorization',
  Initiation: 'initiation',
  AwaitingProjectAuthorization: 'awaiting_project_authorization',
  StageActive: 'stage_active',
  AwaitingNextStageAuthorization: 'awaiting_next_stage_authorization',
  ReadyForClosure: 'ready_for_closure',
  Closed: 'closed',
} as const;
export const ProjectGovernanceStateValues = Object.values(ProjectGovernanceState);
export type ProjectGovernanceState = (typeof ProjectGovernanceState)[keyof typeof ProjectGovernanceState];

/** Requests that the Project Manager submits to the Project Board. */
export const ProjectGovernanceSubmitKind = {
  Initiation: 'INITIATION',
  Project: 'PROJECT',
  StageEnd: 'STAGE_END',
  ExceptionReport: 'EXCEPTION_REPORT',
} as const;
export const ProjectGovernanceSubmitKindValues = Object.values(ProjectGovernanceSubmitKind);
export type ProjectGovernanceSubmitKind =
  (typeof ProjectGovernanceSubmitKind)[keyof typeof ProjectGovernanceSubmitKind];

/** Decisions that the Executive records for the Project Board. */
export const ProjectGovernanceApprovalKind = {
  Initiation: 'INITIATION',
  Project: 'PROJECT',
  Stage: 'STAGE',
  ExceptionPlan: 'EXCEPTION_PLAN',
  Closure: 'CLOSURE',
  PrematureClosure: 'PREMATURE_CLOSURE',
} as const;
export const ProjectGovernanceApprovalKindValues = Object.values(ProjectGovernanceApprovalKind);
export type ProjectGovernanceApprovalKind =
  (typeof ProjectGovernanceApprovalKind)[keyof typeof ProjectGovernanceApprovalKind];

export const ProjectGovernanceDecision = {
  Approved: 'APPROVED',
  Rejected: 'REJECTED',
} as const;
export const ProjectGovernanceDecisionValues = Object.values(ProjectGovernanceDecision);
export type ProjectGovernanceDecision = (typeof ProjectGovernanceDecision)[keyof typeof ProjectGovernanceDecision];

export const ProjectDeliveryMode = {
  Internal: 'internal',
  Supplier: 'supplier',
} as const;
export const ProjectDeliveryModeValues = Object.values(ProjectDeliveryMode);
export type ProjectDeliveryMode = (typeof ProjectDeliveryMode)[keyof typeof ProjectDeliveryMode];

export const ProjectStageStatus = {
  Planned: 'planned',
  Authorized: 'authorized',
  Closed: 'closed',
} as const;
export const ProjectStageStatusValues = Object.values(ProjectStageStatus);
export type ProjectStageStatus = (typeof ProjectStageStatus)[keyof typeof ProjectStageStatus];

/** Who must supply a missing item or record a decision. */
export const ProjectGovernanceRole = {
  BusinessLayer: 'BUSINESS_LAYER',
  Executive: 'EXECUTIVE',
  SeniorUser: 'SENIOR_USER',
  SeniorSupplier: 'SENIOR_SUPPLIER',
  ProjectManager: 'PROJECT_MANAGER',
  ProjectAssurance: 'PROJECT_ASSURANCE',
  DecisionOwner: 'DECISION_OWNER',
} as const;
export type ProjectGovernanceRole = (typeof ProjectGovernanceRole)[keyof typeof ProjectGovernanceRole];

export interface ProjectGovernanceRolesDto {
  executiveUserId: string | null;
  seniorUserUserId: string | null;
  seniorSupplierUserId: string | null;
  projectManagerUserId: string | null;
  /** Optional. When null, the assurance opinion step is not active. */
  projectAssuranceUserId: string | null;
}

/** Board roles that record an opinion on a request. The Executive decides. */
export const BoardOpinionRole = {
  SeniorUser: 'SENIOR_USER',
  SeniorSupplier: 'SENIOR_SUPPLIER',
  ProjectAssurance: 'PROJECT_ASSURANCE',
} as const;
export const BoardOpinionRoleValues = Object.values(BoardOpinionRole);
export type BoardOpinionRole = (typeof BoardOpinionRole)[keyof typeof BoardOpinionRole];

export const BoardOpinionValue = {
  Agree: 'AGREE',
  Object: 'OBJECT',
} as const;
export const BoardOpinionValueValues = Object.values(BoardOpinionValue);
export type BoardOpinionValue = (typeof BoardOpinionValue)[keyof typeof BoardOpinionValue];

/** SAME_AS_EXECUTIVE: the Executive holds this role too, so the decision covers the opinion. */
export const BoardOpinionState = {
  Given: 'GIVEN',
  NotGiven: 'NOT_GIVEN',
  SameAsExecutive: 'SAME_AS_EXECUTIVE',
} as const;
export type BoardOpinionState = (typeof BoardOpinionState)[keyof typeof BoardOpinionState];

export interface BoardOpinionDto {
  role: BoardOpinionRole;
  userId: string;
  state: BoardOpinionState;
  opinion: BoardOpinionValue | null;
  comment: string | null;
  recordedAt: string | null;
}

export const ProjectGovernanceRequestStatus = {
  Pending: 'PENDING',
  Decided: 'DECIDED',
  Stale: 'STALE',
  Withdrawn: 'WITHDRAWN',
} as const;
export const ProjectGovernanceRequestStatusValues = Object.values(ProjectGovernanceRequestStatus);
export type ProjectGovernanceRequestStatus =
  (typeof ProjectGovernanceRequestStatus)[keyof typeof ProjectGovernanceRequestStatus];

/** Frozen copy of the evidence at submit. Financial keys are removed for supplier-side users. */
export interface ProjectGovernanceRequestSnapshot {
  kind: ProjectGovernanceSubmitKind;
  projectTitle: string;
  roles: ProjectGovernanceRolesDto;
  deliveryMode: ProjectDeliveryMode | null;
  stages: { id: string; order: number; title: string; plannedStart: string | null; plannedEnd: string | null; status: ProjectStageStatus }[];
  milestones: { id: number; title: string; stageId: string | null; status: string; dueDate: string | null }[];
  /** Operational figures. A later change shows as an evidence change, not as a stale request. */
  evidence: Record<string, string | number | null>;
  /** Budget and Business Case figures. Customer account only. */
  financial?: Record<string, unknown> | null;
  exceptionReport?: ExceptionReportSnapshot;
}

export interface EvidenceChangeDto {
  key: string;
  before: string | number | null;
  after: string | number | null;
}

export interface ProjectGovernanceRequestDto {
  id: string;
  kind: ProjectGovernanceSubmitKind;
  status: ProjectGovernanceRequestStatus;
  /** True when the baseline changed after submit. The Project Manager must submit again. */
  stale: boolean;
  baselineVersion: number;
  summary: string | null;
  executiveBrief: string | null;
  submittedByUserId: string;
  createdAt: string;
  decidedAt: string | null;
  snapshot: ProjectGovernanceRequestSnapshot;
  opinions: BoardOpinionDto[];
  evidenceChanges: EvidenceChangeDto[];
}

export const GovernanceProjectType = {
  Improvement: 'IMPROVEMENT',
  Transformation: 'TRANSFORMATION',
  Development: 'DEVELOPMENT',
} as const;
export const GovernanceProjectTypeValues = Object.values(GovernanceProjectType);
export type GovernanceProjectType = (typeof GovernanceProjectType)[keyof typeof GovernanceProjectType];

export interface ProjectStageDto {
  id: string;
  order: number;
  title: string;
  plannedStart: string | null;
  plannedEnd: string | null;
  status: ProjectStageStatus;
  authorizedAt: string | null;
  authorizedByApprovalId: string | null;
  /** Null for supplier-side users. */
  plannedBudget: number | null;
}

export interface ProjectGovernanceApprovalDto {
  id: string;
  kind: ProjectGovernanceApprovalKind;
  decision: ProjectGovernanceDecision;
  stageId: string | null;
  /** Governance version that the decision was made on. */
  governanceVersion: number;
  approverUserId: string;
  role: ProjectGovernanceRole;
  rationale: string;
  createdAt: string;
}

/** A missing item that blocks the next submit, with the role that must supply it. */
export interface ProjectGovernanceMissingItemDto {
  code: string;
  owner: ProjectGovernanceRole;
}

export interface ProjectGovernanceDto {
  id: string;
  projectId: string;
  state: ProjectGovernanceState;
  exception: boolean;
  paused: boolean;
  deliveryMode: ProjectDeliveryMode | null;
  projectType: GovernanceProjectType | null;
  decisionCaseId: string | null;
  decisionCaseVersion: number | null;
  roles: ProjectGovernanceRolesDto;
  /** Assigned roles whose holder is not an active member. The business layer must assign a new user. */
  vacantRoles: ProjectGovernanceRole[];
  version: number;
  /** Increases only with baseline changes. A pending request pins it. */
  baselineVersion: number;
  /** Null for supplier-side users. */
  baselineBudget: number | null;
  /** Null for supplier-side users. Part of the baseline. */
  pmChangeLimit: number | null;
  changeAuthorityLimit: number | null;
  changeBudget: number | null;
  changeBudgetUsed: number | null;
  alertResponseDays: number;
  alertBackupUserId: string | null;
  highlightReportWeekday: number;
  gatePrepWorkingDays: number;
  staleUpdateWorkingDays: number;
  pausedAlertDays: number;
  stages: ProjectStageDto[];
  /** The latest pending or stale request, or null. */
  pendingRequest: ProjectGovernanceRequestDto | null;
  approvals: ProjectGovernanceApprovalDto[];
  /** The submit that the current state allows, or null. */
  nextSubmit: ProjectGovernanceSubmitKind | null;
  /** Items that block `nextSubmit`. */
  missing: ProjectGovernanceMissingItemDto[];
  /** Decisions that the Executive can record in the current state. */
  availableDecisions: ProjectGovernanceApprovalKind[];
  permissions: {
    isBusinessLayer: boolean;
    isExecutive: boolean;
    isProjectManager: boolean;
    /** Opinion roles that the user holds and that the Executive does not also hold. */
    opinionRoles: BoardOpinionRole[];
  };
  updatedAt: string;
  /** Set on a write. Null on a read. */
  auditEventId: string | null;
}

export interface UpdateProjectGovernanceRolesDto
  extends Omit<ProjectGovernanceRolesDto, 'projectAssuranceUserId'> {
  /** Omitted keeps the current value. */
  projectAssuranceUserId?: string | null;
  expectedVersion: number;
}

export interface SetProjectTypeDto {
  projectType: GovernanceProjectType | null;
}

export interface RecordBoardOpinionDto {
  role: BoardOpinionRole;
  opinion: BoardOpinionValue;
  comment?: string | null;
}

export const ApprovalInboxAction = {
  Decide: 'DECIDE',
  Opinion: 'OPINION',
  Resubmit: 'RESUBMIT',
} as const;
export type ApprovalInboxAction = (typeof ApprovalInboxAction)[keyof typeof ApprovalInboxAction];

export interface ApprovalInboxItemDto {
  projectId: string;
  projectTitle: string;
  projectCode: string | null;
  request: ProjectGovernanceRequestDto;
  /** Roles of the current user on this project. */
  myRoles: ProjectGovernanceRole[];
  /** Empty when the item is only visible to the user. */
  myActions: ApprovalInboxAction[];
}

export interface ApprovalInboxDto {
  mine: ApprovalInboxItemDto[];
  other: ApprovalInboxItemDto[];
}

export interface SetProjectDeliveryModeDto {
  mode: ProjectDeliveryMode;
  expectedVersion: number;
}

export interface UpsertProjectStageItemDto {
  id?: string;
  title: string;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  plannedBudget?: number | null;
}

/** Replaces the planned stages. Authorized and closed stages do not change. */
export interface UpsertProjectStagesDto {
  expectedVersion: number;
  stages: UpsertProjectStageItemDto[];
}

export interface CreateGovernedMilestoneDto {
  expectedVersion: number;
  stageId: string;
  title: string;
  startDate?: string | null;
  dueDate?: string | null;
  output?: string | null;
  acceptanceCriteria?: string | null;
  teamManagerUserId: string;
  /** Defaults to the Senior User. */
  acceptanceUserId?: string | null;
  /** Defaults to MUST. */
  priority?: MilestonePriority;
  durationWorkingDays?: number | null;
}

export interface UpdateGovernedMilestoneDto {
  expectedVersion: number;
  stageId?: string;
  title?: string;
  startDate?: string | null;
  dueDate?: string | null;
  output?: string | null;
  acceptanceCriteria?: string | null;
  teamManagerUserId?: string;
  acceptanceUserId?: string | null;
  priority?: MilestonePriority;
  durationWorkingDays?: number | null;
}

export interface ProjectMilestoneDependencyDto {
  id: string;
  predecessorMilestoneId: number;
  successorMilestoneId: number;
  lagWorkingDays: number;
}

export interface ProjectMilestoneDependencyInputDto {
  predecessorMilestoneId: number;
  successorMilestoneId: number;
  lagWorkingDays?: number;
}

export interface SetProjectMilestoneDependenciesDto {
  expectedVersion: number;
  dependencies: ProjectMilestoneDependencyInputDto[];
}

export interface ProjectScheduleMilestoneDto {
  id: number;
  earliestStart: string;
  earliestFinish: string;
  totalFloat: number;
  isCritical: boolean;
}

export interface ProjectScheduleDto {
  projectStart: string;
  projectEnd: string | null;
  criticalPathMilestoneIds: number[];
  milestones: ProjectScheduleMilestoneDto[];
}

/** True when MARK_IN_PROGRESS must wait. FS predecessors win when the project has any dependency row. */
export function isMilestoneStartBlocked(
  milestoneId: number,
  dependencies: { predecessorMilestoneId: number; successorMilestoneId: number }[],
  milestones: { id: number | string; status: string }[],
): boolean {
  const rows = milestones.map((m) => ({ id: Number(m.id), status: m.status }));
  if (dependencies.length > 0) {
    return dependencies
      .filter((d) => d.successorMilestoneId === milestoneId)
      .some((d) => {
        const pred = rows.find((m) => m.id === d.predecessorMilestoneId);
        return !pred || pred.status !== 'Completed';
      });
  }
  const ordered = [...rows].sort((a, b) => a.id - b.id);
  const index = ordered.findIndex((m) => m.id === milestoneId);
  if (index <= 0) return false;
  return ordered[index - 1].status !== 'Completed';
}

/** Moves a milestone to a stage. Contract milestones without a stage can join the authorized stage. */
export interface AssignMilestoneStageDto {
  expectedVersion: number;
  stageId: string;
}

export interface SubmitProjectGovernanceDto {
  kind: ProjectGovernanceSubmitKind;
  expectedVersion: number;
  /** Project Manager summary for the Board. */
  summary?: string | null;
}

export interface DecideProjectGovernanceDto {
  kind: ProjectGovernanceApprovalKind;
  decision: ProjectGovernanceDecision;
  rationale: string;
  expectedVersion: number;
  /** Follow-on actions recorded on the End Project Report or premature closure product. */
  followOnActions?: string[] | null;
}

export interface SetProjectGovernanceFlagsDto {
  expectedVersion: number;
  exception?: boolean;
  paused?: boolean;
}

export interface SetCommunicationPlanDto {
  expectedVersion: number;
  alertResponseDays: number;
  alertBackupUserId?: string | null;
  highlightReportWeekday: number;
  gatePrepWorkingDays: number;
  staleUpdateWorkingDays: number;
  pausedAlertDays: number;
}
